'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { newrelic } from '@hen-site/lib/newrelic/client';
import { subscribeMarketingConsent } from '@hen-site/lib/paid-social/consent';
import { ONETRUST_GROUPS_UPDATED_EVENT } from '@hen-site/lib/paid-social/constants';
import { installFbqStub } from '@hen-site/lib/paid-social/fbqStub';
import { shouldLoadGroasForPath } from '@hen-site/lib/paid-social/groasContentPath';
import {
  recordGroasPage,
  reloadWithoutGroas,
  shouldReloadForGroasNavigation,
} from '@hen-site/lib/paid-social/groasNavigation';
import {
  loadGoogleAdsTag,
  loadGroasContent,
  loadLinkedInInsight,
  loadMetaPixel,
} from '@hen-site/lib/paid-social/loadVendorScripts';
import type {
  MarketingConsentState,
  PaidSocialEmitter,
} from '@hen-site/lib/paid-social/types';
import { matchesViewContentPath } from '@hen-site/lib/paid-social/viewContentPaths';
import { usePaidSocialConversions } from './usePaidSocialConversions';
import { usePaidSocialEmitter } from './usePaidSocialEmitter';

export interface PaidSocialBootstrapProps {
  readonly pixelId?: string;
  readonly partnerId?: string;
  readonly conversionId?: string;
  readonly groasEnabled?: boolean;
  readonly tenant?: string;
  readonly emitter?: PaidSocialEmitter | null;
}

/**
 * Returns a trimmed vendor id, or undefined when the value is empty.
 *
 * @param {string | undefined} value - Raw tenant-config identifier.
 * @returns {string | undefined} Trimmed id, or undefined.
 */
const resolveVendorId = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

/**
 * Builds the page URL the same way the eventing provider does.
 *
 * @param {string} pathname - App Router pathname.
 * @returns {string} Absolute URL, or the pathname when `window` is unavailable.
 */
const toPageUrl = (pathname: string): string => {
  return typeof window !== 'undefined'
    ? `${window.location.origin}${pathname}`
    : pathname;
};

/**
 * Consent-gated Meta Pixel, LinkedIn Insight, Google Ads gtag, and GROAS
 * content loader. Renders nothing. Installs the inert `fbq` queue stub on
 * mount, subscribes to marketing consent only when a vendor id or the GROAS
 * flag is configured, loads scripts only after consent, and emits PageView /
 * ViewContent through the optional emitter seam when a vendor id is
 * configured. Dynamic Experience conversions use the same seam via
 * `usePaidSocialConversions`. Google Ads loads `gtag.js` only; it does not
 * emit a conversion event. GROAS does not call `fbq`, `lintrk`, or `gtag`.
 * Path-restricted tenants load GROAS only on allowlisted paths.
 *
 * @param {PaidSocialBootstrapProps} props - Vendor ids, GROAS flag, tenant, and optional test emitter.
 * @returns {null} Always null.
 */
export const PaidSocialBootstrap = ({
  pixelId,
  partnerId,
  conversionId,
  groasEnabled = false,
  tenant,
  emitter,
}: PaidSocialBootstrapProps): null => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const pageLocation = pathname
    ? `${pathname}${search ? `?${search}` : ''}`
    : null;
  const seamEmitter = usePaidSocialEmitter();
  const activeEmitter = emitter !== undefined ? emitter : seamEmitter;
  usePaidSocialConversions(activeEmitter);
  const metaPixelId = resolveVendorId(pixelId);
  const linkedInPartnerId = resolveVendorId(partnerId);
  const googleAdsConversionId = resolveVendorId(conversionId);
  const loadGroas =
    groasEnabled === true && shouldLoadGroasForPath(tenant, pathname);
  const hasVendorId =
    Boolean(metaPixelId) ||
    Boolean(linkedInPartnerId) ||
    Boolean(googleAdsConversionId);
  const shouldSubscribe = hasVendorId || loadGroas;

  const [consentState, setConsentState] =
    useState<MarketingConsentState>('unknown');
  const lastEmittedUrlRef = useRef<string | null>(null);

  useEffect(() => {
    installFbqStub();

    // Consent state is only read by the loader and emit effects. Subscribing
    // without a vendor id or the GROAS flag would poll OneTrust for the full
    // budget on every page load to no effect.
    if (!shouldSubscribe) {
      return;
    }

    const unsubscribe = subscribeMarketingConsent(setConsentState, {
      onPollTimeout: () => {
        newrelic.noticeError?.(new Error('MarketingConsentUnresolved'), {
          hasPixelId: Boolean(metaPixelId),
          hasPartnerId: Boolean(linkedInPartnerId),
          hasConversionId: Boolean(googleAdsConversionId),
          hasGroas: loadGroas,
        });
      },
    });

    return unsubscribe;
  }, [
    shouldSubscribe,
    googleAdsConversionId,
    linkedInPartnerId,
    loadGroas,
    metaPixelId,
  ]);

  useEffect(() => {
    if (consentState !== 'granted') {
      lastEmittedUrlRef.current = null;
    }
  }, [consentState]);

  useEffect(() => {
    if (shouldReloadForGroasNavigation(tenant, pageLocation)) {
      reloadWithoutGroas();
    }
  }, [pageLocation, tenant]);

  useEffect(() => {
    if (consentState !== 'granted') {
      return;
    }
    if (metaPixelId) {
      loadMetaPixel(metaPixelId);
    }
    if (linkedInPartnerId) {
      loadLinkedInInsight(linkedInPartnerId);
    }
    if (googleAdsConversionId) {
      loadGoogleAdsTag(googleAdsConversionId);
    }
    if (loadGroas) {
      loadGroasContent();
      recordGroasPage(tenant, pageLocation);
    }
  }, [
    consentState,
    googleAdsConversionId,
    linkedInPartnerId,
    loadGroas,
    metaPixelId,
    pageLocation,
    tenant,
  ]);

  useEffect(() => {
    if (consentState !== 'granted' || !googleAdsConversionId) {
      return;
    }

    // OneTrust can redefine `window.gtag` after the loader ran, which would
    // route gtag calls into its own queue and transmit nothing. A repeat groups
    // update leaves `consentState` at `granted`, so React bails out of the load
    // effect above and only this listener reclaims ownership.
    const reclaimGtagOwnership = (): void => {
      loadGoogleAdsTag(googleAdsConversionId);
    };

    window.addEventListener(
      ONETRUST_GROUPS_UPDATED_EVENT,
      reclaimGtagOwnership,
    );

    return () => {
      window.removeEventListener(
        ONETRUST_GROUPS_UPDATED_EVENT,
        reclaimGtagOwnership,
      );
    };
  }, [consentState, googleAdsConversionId]);

  useEffect(() => {
    if (consentState !== 'granted' || !hasVendorId || !activeEmitter) {
      return;
    }
    if (!pathname) {
      return;
    }

    const pageUrl = toPageUrl(pathname);
    if (lastEmittedUrlRef.current === pageUrl) {
      return;
    }

    // A marketing emitter must never be able to fault the tenant layout, and
    // the url is recorded only after both emits succeed so a failed emit is
    // retried rather than treated as already sent.
    try {
      activeEmitter.trackPageView(pageUrl);

      if (matchesViewContentPath(pathname)) {
        activeEmitter.trackConversion({ name: 'ViewContent', pageUrl });
      }

      lastEmittedUrlRef.current = pageUrl;
    } catch (error) {
      newrelic.noticeError?.(
        new Error('PaidSocialEmitFailed', { cause: error }),
      );
    }
  }, [activeEmitter, consentState, hasVendorId, pathname]);

  return null;
};
